// components/Checkout.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  
  // 🔥 TUS CLAVES API DE WOOCOMMERCE
  const CONSUMER_KEY = 'ck_10a7f64bb813e5c80da979e6c7aec6d878caa883';
  const CONSUMER_SECRET = 'cs_ca1236282d5689c22bb93e0122c5650937500cee';
  
  // 🔥 URL BASE - PRUEBA CON DIFERENTES OPCIONES:
  const WC_API_BASE = 'http://localhost/wp-json/wc/v3'; // Si estás en local
  // const WC_API_BASE = 'https://tudominio.com/wp-json/wc/v3'; // Tu dominio real
  // const WC_API_BASE = '/wp-json/wc/v3'; // Ruta relativa si está en el mismo dominio

  const [formData, setFormData] = useState({
    billing_first_name: '',
    billing_last_name: '',
    billing_company: '',
    billing_country: 'ES',
    billing_address_1: '',
    billing_address_2: '',
    billing_postcode: '',
    billing_city: '',
    billing_state: '',
    billing_phone: '',
    billing_email: '',
    ship_to_different_address: false,
    order_comments: '',
    payment_method: 'cod'
  });

  // 🔥 OBTENER PAÍSES Y PROVINCIAS DESDE TU WOOCOMMERCE REAL
  useEffect(() => {
    const fetchCountriesData = async () => {
      try {
        setLoadingData(true);
        
        console.log('Intentando conectar a:', `${WC_API_BASE}/data/countries`);
        
        // PRIMERO: Probar con endpoint público de países
        let countriesData = [];
        
        try {
          // Intento 1: Endpoint público de WooCommerce
          const publicResponse = await fetch(`/wp-json/wc/v3/data/countries`);
          if (publicResponse.ok) {
            countriesData = await publicResponse.json();
            console.log('✅ Países obtenidos via endpoint público:', countriesData);
          } else {
            throw new Error('Endpoint público falló');
          }
        } catch (publicError) {
          console.log('Endpoint público falló, intentando con autenticación...');
          
          // Intento 2: Con autenticación
          const authResponse = await fetch(
            `${WC_API_BASE}/data/countries`,
            {
              method: 'GET',
              headers: {
                'Authorization': 'Basic ' + btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`),
                'Content-Type': 'application/json',
              }
            }
          );

          if (authResponse.ok) {
            countriesData = await authResponse.json();
            console.log('✅ Países obtenidos via autenticación:', countriesData);
          } else {
            console.error('❌ Error en autenticación:', authResponse.status, await authResponse.text());
            throw new Error(`HTTP ${authResponse.status}`);
          }
        }

        if (countriesData && countriesData.length > 0) {
          // Formatear países para el dropdown
          const formattedCountries = countriesData.map(country => ({
            code: country.code,
            name: country.name
          }));
          
          setCountries(formattedCountries);

          // Obtener estados/provincias para cada país
          const statesData = {};
          for (const country of countriesData) {
            if (country.states && Object.keys(country.states).length > 0) {
              statesData[country.code] = Object.entries(country.states).map(([code, name]) => ({
                code: code,
                name: name
              }));
            }
          }
          
          setStates(statesData);
          console.log('Estados/provincias obtenidos:', statesData);
        } else {
          console.warn('No se obtuvieron países de la API');
          loadFallbackData();
        }
        
      } catch (error) {
        console.error('Error completo obteniendo países:', error);
        loadFallbackData();
      } finally {
        setLoadingData(false);
      }
    };

    const loadFallbackData = () => {
      console.log('Cargando datos de respaldo...');
      // Datos de respaldo mínimos
      const fallbackCountries = [
        { code: 'ES', name: 'España' },
        { code: 'US', name: 'United States' },
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'IT', name: 'Italy' }
      ];

      const fallbackStates = {
        ES: [
          { code: 'M', name: 'Madrid' },
          { code: 'B', name: 'Barcelona' },
          { code: 'V', name: 'Valencia' },
          { code: 'SE', name: 'Sevilla' },
          { code: 'BI', name: 'Vizcaya' }
        ]
      };

      setCountries(fallbackCountries);
      setStates(fallbackStates);
    };

    fetchCountriesData();
  }, []);

  // 🔥 OBTENER PROVINCIAS SEGÚN EL PAÍS SELECCIONADO
  const getStatesForCountry = (countryCode) => {
    return states[countryCode] || [];
  };

  // 🔥 CALCULAR TOTALES
  const calculateTotals = () => {
    const subtotal = cart.reduce((total, item) => {
      const price = parseFloat(item.sale_price || item.price);
      return total + (price * item.quantity);
    }, 0);
    
    const shipping = 5.00;
    const tax = subtotal * 0.21;
    const total = subtotal + shipping;

    return { 
      subtotal: subtotal.toFixed(2), 
      shipping: shipping.toFixed(2), 
      tax: tax.toFixed(2), 
      total: total.toFixed(2) 
    };
  };

  // 🔥 MANEJAR CAMBIOS EN EL FORMULARIO
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Si cambia el país, resetear la provincia
      if (name === 'billing_country') {
        newData.billing_state = '';
      }
      
      return newData;
    });
  };

  // 🔥 PROCESAR PEDIDO
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      alert('Tu carrito está vacío');
      return;
    }

    // Validar campos requeridos
    const requiredFields = [
      'billing_first_name',
      'billing_last_name', 
      'billing_address_1',
      'billing_postcode',
      'billing_city',
      'billing_phone',
      'billing_email'
    ];

    // Solo validar estado si el país tiene estados
    const currentStates = getStatesForCountry(formData.billing_country);
    if (currentStates.length > 0) {
      requiredFields.push('billing_state');
    }

    const missingFields = requiredFields.filter(field => !formData[field]?.trim());
    
    if (missingFields.length > 0) {
      alert('Por favor, completa todos los campos requeridos');
      return;
    }

    setLoading(true);

    try {
      // Simular procesamiento (por ahora)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('¡Pedido realizado con éxito! Recibirás un email de confirmación.');
      clearCart();
      navigate('/');
      
    } catch (error) {
      alert('Error al procesar el pedido. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 RENDERIZAR INFORMACIÓN ADICIONAL
  const renderAdditionalInfo = () => (
    <div style={formSectionStyle}>
      <h3 style={sectionTitleStyle}>Información adicional</h3>
      
      <div style={formGroupStyle}>
        <label style={labelStyle}>Notas del pedido (opcional)</label>
        <textarea
          name="order_comments"
          value={formData.order_comments}
          onChange={handleInputChange}
          style={{ ...inputStyle, minHeight: '100px' }}
          placeholder="Notas sobre tu pedido, por ejemplo, notas especiales para la entrega."
        />
      </div>
    </div>
  );

  // 🔥 RENDERIZAR RESUMEN DEL PEDIDO
  const renderOrderSummary = () => {
    const { subtotal, shipping, tax, total } = calculateTotals();

    return (
      <div style={orderSummaryStyle}>
        <h3 style={sectionTitleStyle}>Tu pedido</h3>
        
        <div style={orderProductsStyle}>
          {cart.map((item, index) => (
            <div key={index} style={orderProductStyle}>
              <div style={productInfoStyle}>
                <span style={productNameStyle}>
                  {item.name} {item.attributes && `- ${item.attributes.join(', ')}`}
                </span>
                <strong style={productQuantityStyle}>× {item.quantity}</strong>
              </div>
              <div style={productPriceStyle}>
                {((item.sale_price || item.price) * item.quantity).toFixed(2).replace('.', ',')} €
              </div>
            </div>
          ))}
        </div>

        <div style={orderTotalsStyle}>
          <div style={totalRowStyle}>
            <span>Subtotal</span>
            <span>{subtotal.replace('.', ',')} €</span>
          </div>
          
          <div style={shippingSectionStyle}>
            <div style={shippingTitleStyle}>Envío</div>
            <div style={shippingMethodsStyle}>
              <label style={shippingMethodStyle}>
                <input
                  type="radio"
                  name="shipping_method"
                  value="flat_rate"
                  checked
                  readOnly
                  style={radioStyle}
                />
                Precio fijo: {shipping.replace('.', ',')} €
              </label>
            </div>
          </div>

          <div style={grandTotalStyle}>
            <span>Total</span>
            <span>{total.replace('.', ',')} €</span>
          </div>
          
          <div style={taxInfoStyle}>
            (incluye {tax.replace('.', ',')} € IVA)
          </div>
        </div>

        <div style={paymentSectionStyle}>
          <h4 style={paymentTitleStyle}>Contra reembolso</h4>
          <p style={paymentDescriptionStyle}>
            Paga en efectivo en el momento de la entrega.
          </p>
        </div>

        <div style={privacyStyle}>
          <p style={privacyTextStyle}>
            Tus datos personales se utilizarán para procesar tu pedido, mejorar tu experiencia 
            en esta web y otros propósitos descritos en nuestra política de privacidad.
          </p>
        </div>

        <button 
          type="submit"
          disabled={loading || cart.length === 0}
          style={{
            ...submitButtonStyle,
            opacity: (loading || cart.length === 0) ? 0.6 : 1,
            cursor: (loading || cart.length === 0) ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Procesando pedido...' : 'Realizar pedido'}
        </button>
      </div>
    );
  };

  // 🔥 RENDERIZAR FORMULARIO DE FACTURACIÓN
  const renderBillingForm = () => {
    const currentStates = getStatesForCountry(formData.billing_country);
    const hasStates = currentStates.length > 0;

    return (
      <div style={formSectionStyle}>
        <h3 style={sectionTitleStyle}>Detalles de facturación</h3>
        
        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Nombre *</label>
            <input
              type="text"
              name="billing_first_name"
              value={formData.billing_first_name}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />
          </div>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Apellidos *</label>
            <input
              type="text"
              name="billing_last_name"
              value={formData.billing_last_name}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Nombre de la empresa (opcional)</label>
          <input
            type="text"
            name="billing_company"
            value={formData.billing_company}
            onChange={handleInputChange}
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>País / Región *</label>
          <select 
            name="billing_country" 
            value={formData.billing_country}
            onChange={handleInputChange}
            style={inputStyle}
            required
          >
            <option value="">Selecciona un país...</option>
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          {countries.length === 0 && (
            <div style={errorTextStyle}>
              No se pudieron cargar los países desde WooCommerce. Usando lista básica.
            </div>
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Dirección de la calle *</label>
          <input
            type="text"
            name="billing_address_1"
            value={formData.billing_address_1}
            onChange={handleInputChange}
            required
            style={inputStyle}
            placeholder="Nombre de la calle y número de la casa"
          />
          <input
            type="text"
            name="billing_address_2"
            value={formData.billing_address_2}
            onChange={handleInputChange}
            style={{ ...inputStyle, marginTop: '10px' }}
            placeholder="Apartamento, suite, unidad, etc. (opcional)"
          />
        </div>

        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Código postal *</label>
            <input
              type="text"
              name="billing_postcode"
              value={formData.billing_postcode}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />
          </div>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Población *</label>
            <input
              type="text"
              name="billing_city"
              value={formData.billing_city}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>
            Provincia {hasStates ? '*' : ''}
          </label>
          {hasStates ? (
            <select 
              name="billing_state" 
              value={formData.billing_state}
              onChange={handleInputChange}
              style={inputStyle}
              required
            >
              <option value="">Selecciona una provincia...</option>
              {currentStates.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              name="billing_state"
              value={formData.billing_state}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder={formData.billing_country ? "Provincia/Estado (opcional)" : "Selecciona un país primero"}
              disabled={!formData.billing_country}
            />
          )}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Teléfono *</label>
          <input
            type="tel"
            name="billing_phone"
            value={formData.billing_phone}
            onChange={handleInputChange}
            required
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Dirección de correo electrónico *</label>
          <input
            type="email"
            name="billing_email"
            value={formData.billing_email}
            onChange={handleInputChange}
            required
            style={inputStyle}
          />
        </div>

        <div style={checkboxGroupStyle}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              name="ship_to_different_address"
              checked={formData.ship_to_different_address}
              onChange={handleInputChange}
              style={checkboxStyle}
            />
            ¿Enviar a una dirección diferente?
          </label>
        </div>
      </div>
    );
  };

  // 🔥 ESTILOS (los mismos del código anterior)
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    background: '#f5f5f5',
    minHeight: '100vh',
    fontSize: '14px'
  };

  const breadcrumbStyle = {
    marginBottom: '20px'
  };

  const backButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#0073aa',
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline',
    padding: 0
  };

  const pageTitleStyle = {
    color: '#333',
    marginBottom: '30px',
    fontSize: '24px',
    fontWeight: '400',
    textAlign: 'left'
  };

  const formStyle = {
    background: 'transparent'
  };

  const checkoutLayoutStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '40px',
    alignItems: 'start'
  };

  const formsColumnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  };

  const summaryColumnStyle = {
    position: 'sticky',
    top: '20px'
  };

  const formSectionStyle = {
    background: 'white',
    padding: '25px',
    borderRadius: '0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #ddd'
  };

  const sectionTitleStyle = {
    marginBottom: '20px',
    color: '#333',
    fontSize: '18px',
    fontWeight: '600',
    paddingBottom: '10px',
    borderBottom: '1px solid #ddd'
  };

  const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginBottom: '15px'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: '#333',
    fontSize: '13px'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '3px',
    fontSize: '13px',
    boxSizing: 'border-box',
    lineHeight: '1.4'
  };

  const checkboxGroupStyle = {
    marginBottom: '15px'
  };

  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#555'
  };

  const checkboxStyle = {
    marginRight: '8px',
    width: '16px',
    height: '16px'
  };

  const radioStyle = {
    marginRight: '8px'
  };

  const orderSummaryStyle = {
    background: 'white',
    padding: '25px',
    border: '1px solid #ddd',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  };

  const orderProductsStyle = {
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px'
  };

  const orderProductStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '8px 0',
    fontSize: '13px'
  };

  const productInfoStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'column'
  };

  const productNameStyle = {
    fontSize: '13px',
    color: '#333',
    marginBottom: '4px'
  };

  const productQuantityStyle = {
    fontSize: '12px',
    color: '#777'
  };

  const productPriceStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginLeft: '10px'
  };

  const orderTotalsStyle = {
    borderTop: '1px solid #eee',
    paddingTop: '15px',
    marginBottom: '20px'
  };

  const totalRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '13px',
    color: '#555'
  };

  const shippingSectionStyle = {
    margin: '15px 0'
  };

  const shippingTitleStyle = {
    fontWeight: '600',
    marginBottom: '8px',
    fontSize: '13px'
  };

  const shippingMethodsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const shippingMethodStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#555'
  };

  const grandTotalStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '2px solid #333',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  };

  const taxInfoStyle = {
    fontSize: '12px',
    color: '#777',
    textAlign: 'right',
    marginTop: '5px'
  };

  const paymentSectionStyle = {
    background: '#f8f8f8',
    padding: '15px',
    marginBottom: '20px',
    border: '1px solid #e0e0e0'
  };

  const paymentTitleStyle = {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  };

  const paymentDescriptionStyle = {
    margin: '0',
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.4'
  };

  const privacyStyle = {
    marginBottom: '20px',
    padding: '15px',
    background: '#f8f8f8',
    border: '1px solid #e0e0e0'
  };

  const privacyTextStyle = {
    margin: '0',
    fontSize: '12px',
    color: '#666',
    lineHeight: '1.4'
  };

  const submitButtonStyle = {
    width: '100%',
    background: '#0073aa',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '3px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.3s ease'
  };

  const errorTextStyle = {
    color: '#d63638',
    fontSize: '12px',
    marginTop: '5px',
    fontStyle: 'italic'
  };

  // 🔥 RENDERIZAR CHECKOUT COMPLETO
  const renderWoocommerceCheckout = () => (
    <div style={containerStyle}>
      <div style={breadcrumbStyle}>
        <button onClick={() => navigate('/carrito')} style={backButtonStyle}>
          ← Volver al carrito
        </button>
      </div>

      <h1 style={pageTitleStyle}>Finalizar compra</h1>

      <form onSubmit={handlePlaceOrder} style={formStyle}>
        <div style={checkoutLayoutStyle}>
          <div style={formsColumnStyle}>
            {renderBillingForm()}
            {renderAdditionalInfo()}
          </div>
          <div style={summaryColumnStyle}>
            {renderOrderSummary()}
          </div>
        </div>
      </form>
    </div>
  );

  return renderWoocommerceCheckout();
};

export default Checkout;